/* ==========================================================================
   Monaco Social Club — Interactive size chart
   --------------------------------------------------------------------------
   Progressive enhancement over a merchant-authored <table> inside the size
   guide page content. If the table is missing or unparseable, the original
   page content is left completely untouched.

   Source table conventions (typed in the Shopify page editor):
     - Either orientation works:
         rows = sizes, columns = measurements   OR
         rows = measurements, columns = sizes
     - Numbers are read as CENTIMETRES by default. To use inches, mark the
       table any ONE of these ways (whichever Shopify's editor preserves):
         <table data-unit="in">          or
         <table class="unit-in">         or
         <caption>Measured in inches</caption> inside the table
     - Ranges ("86-91") and decimals are supported.

   The fit finder uses plain text fields. They are safe here only because of
   the guard below.

   IMPORTANT: this component sits inside <product-variant-selects>, which
   treats any bubbling `change` event as a variant selection. All `change`
   and `input` events are stopped at the <size-chart> boundary — see
   connectedCallback. Removing that will make the drawer close whenever a
   form control inside it is used.

   No dependencies.
   ========================================================================== */

(() => {
  'use strict';
  if (customElements.get('size-chart')) return;

  /* ---------------------------------------------------------------------
     FIT FINDER ON/OFF
     Set to false to ship the chart on its own — the "Find your size" card is
     not built at all, and the unit toggle plus the table work exactly as
     normal. Flip back to true once the FIT numbers below are trusted.
     --------------------------------------------------------------------- */
  const SHOW_FIT_FINDER = false;

  /* ---------------------------------------------------------------------
     FIT MODEL v3 — calibrated on 4 fit testers (fit-data.csv, 5 Oct 2026)
     1. Estimate the shopper's body chest from height + weight:
          chest (cm) = CHEST_K × √(weight kg) ÷ height cm^0.25
        Within 2 cm on every tester. Pure isometric scaling would use
        height^0.5, but that drops the estimate 8 cm across a 30 cm height
        range and made the recommendation shrink for taller shoppers; the
        testers' heights are too close together to tell the two apart, so the
        gentler exponent is used and height is handled by the length floor.
     2. Each cut lists the body chest up to which each size is right
        (FIT_BY_CUT). The bands were placed so that every tester gets the
        size they actually wear — they are wider at the top because two very
        different bodies (Alex 103 cm, Jimmy 110 cm) both wear XL.
     3. Pages are mapped to cuts by handle (CUT_BY_HANDLE); an unknown page
        is matched on the chart's own numbers (`sig`).
     4. A chart that matches neither, but has a Chest row, falls back to
        "garment chest minus body chest closest to DEFAULT_EASE".
     Charts are identified by the size-guide page handle (data-chart, set in
     snippets/main-product__variant-picker.liquid).
     To tune: change the numbers below. Nothing else.
     --------------------------------------------------------------------- */
  const CHEST_K = 40.33;
  const CHEST_HEIGHT_EXP = 0.25;

  /* Above this height the chest estimate stops shrinking. Two reasons: a very
     tall shopper should never be offered a SMALLER size than a shorter one of
     the same weight, and on an oversized line erring large is the safe side.
     Height keeps working above it through the length floor. */
  const CHEST_HEIGHT_CAP = 180;

  /* One entry per cut. `chestUpTo` is the body chest (cm) up to which each
     size is right — above the last one listed, the chart's largest size.
     `sig` is the chart's own fingerprint (see below), used when the page
     handle is unknown. */
  const FIT_BY_CUT = {
    rawHem:     { chestUpTo: { S: 88, M: 96, L: 101, XL: 112 },              room: 24, sig: 'S/M/L/XL/2XL|57/60/62/64/68', length: [0.389, 0.429], verified: ['S', 'L', 'XL'] },
    boxy:       { chestUpTo: { S: 87, M: 95, L: 101, XL: 113 },              room: 28, sig: 'S/M/L/XL/2XL|61/63/65/67/69', length: [0.363, 0.402], verified: ['S', 'L', 'XL'] },
    heavyCore:  { chestUpTo: { XS: 86, S: 91, M: 96, L: 101, XL: 113 },      room: 22, sig: 'XS/S/M/L/XL/2XL|56/58/60/62/64/66', length: [0.411, 0.425], verified: ['XS', 'L', 'XL'] },
    paneled:    { chestUpTo: { S: 87, M: 95, L: 101, XL: 113, '2XL': 117 },  room: 20, sig: 'S/M/L/XL/2XL/3XL|57/59/61/63/65/67', length: [0.375, 0.415], verified: ['S', 'L', 'XL'] },
    athleisure: { chestUpTo: { XS: 86, S: 91, M: 96, L: 101, XL: 114 },      room: 34, sig: 'XS/S/M/L/XL/2XL|61/63/65/67/70/73', length: [0.348, 0.397], verified: ['XS', 'S', 'L', 'XL'] },
    fullZip:    { chestUpTo: { XS: 86, S: 91, M: 96, L: 101, XL: 114 },      room: 34, sig: 'XS/S/M/L/XL/2XL|61/63/65/67/70/73', length: [0.348, 0.397], verified: ['XS', 'L', 'XL'] },
    quarterZip: { chestUpTo: { XS: 87, S: 95, M: 101, L: 113 },              room: 30, sig: 'XS/S/M/L/XL|58/61/65/68/70', length: [0.385, 0.422], verified: ['XS', 'M', 'L'] },
    joggers:    { chestUpTo: { S: 91, M: 101, L: 113, XL: 117 },             sig: 'S/M/L/XL/2XL|110/114/118/122/126', length: [0.578, 0.612], verified: ['S', 'M', 'L'] },
    short:      { chestUpTo: { S: 91, M: 101, L: 107, XL: 115 },             sig: 'S/M/L/XL/2XL|64/68/72/76/80', length: [0.240, 0.264], verified: ['S', 'M', 'L', 'XL'] }
  };

  /* Every size-guide page handle that serves each cut. Add a line when a new
     page is created — several handles may point at the same cut. */
  const CUT_BY_HANDLE = {
    /* test shop */
    'size-chart-test':                  'rawHem',
    'test-size-boxy':                   'boxy',
    'test-size-gen3':                   'heavyCore',
    'test-size-paneled':                'paneled',
    'test-size-athleisure':             'athleisure',
    'test-size-zip-up':                 'fullZip',
    'test-size-quarter-zip':            'quarterZip',
    'test-size-sweatpants':             'joggers',
    'sweatshort-test-size':             'short',
    /* live theme */
    'size-chart-hoodie':                'rawHem',
    'size-chart-classic-boxy-tee':      'boxy',
    'size-chart-gen3':                  'heavyCore',
    'size-chart':                       'paneled',
    'size-chart-hoodie-athleisure':     'athleisure',
    'size-chart-full-zip-hoodie':       'fullZip',
    'size-chart-quarter-zip':           'quarterZip',
    'size-chart-athleisure-sweatpants': 'joggers',
    'size-chart-joggers':               'joggers',
    'size-chart-sweatshorts-gen3':      'short'
  };

  /* Fingerprint of a chart: its size labels plus its Chest row (or Waist row
     for bottoms) in whole centimetres. Used when the page handle is missing or
     renamed, so the right numbers are still found. */
  const signature = (sizes, metrics, sourceUnit) => {
    const row =
      metrics.find((m) => m.numeric && /chest|poitrine/i.test(m.label)) ||
      metrics.find((m) => m.numeric && /waist|taille/i.test(m.label)) ||
      metrics.find((m) => m.numeric);
    if (!row) return null;
    const cm = row.values.map((v) => {
      const n = firstNumber(v);
      return n == null ? '?' : String(Math.round(sourceUnit === 'in' ? n * 2.54 : n));
    });
    return `${sizes.join('/')}|${cm.join('/')}`;
  };

  /* Body length ÷ height that each cut's testers called "good", and the sizes
     a real person has actually worn. Outside those, the finder says so instead
     of pretending to be sure. */
  /* Slack on the length ranges, as a fraction of the ratio itself, so it works
     the same for a 70 cm t-shirt body, a 110 cm jogger outseam and a 45 cm short.
     SHORT is tight — one size step is about 3% — while LONG is lenient, because
     on an oversized cut extra length is the look, not a fault. */
  const LENGTH_SLACK = { short: 0.02, long: 0.06 };

  /* How many sizes the length floor may add on top of the chest answer. */
  const MAX_SIZE_UP = 2;

  /* A garment measurement in the unit the shopper is reading the chart in.
     Returns null when it rounds away to nothing, so we never print "0″". */
  const asUnit = (cm, unit) => {
    const n = Math.round(unit === 'in' ? cm / 2.54 : cm);
    return n < 1 ? null : unit === 'in' ? `${n}″` : `${n} cm`;
  };

  /* A top whose page isn't listed above still gets a recommendation,
     using the middle of the calibrated range. */
  const DEFAULT_EASE = 28;

  /* Closer than this (cm) to the halfway point between two sizes →
     tell the shopper they are between sizes. */
  const BETWEEN_CM = 1.5;

  /* Smallest size still has this much more room than intended (or the
     largest this much less) → say so instead of pretending it is perfect. */
  const OFF_RANGE_CM = 6;

  /* Sits under every recommendation. Keep it short and keep it confident —
     it hands the decision back to the shopper rather than hedging. */
  const FOOTNOTE = 'A starting point, not a rule — you know how you like to wear it. Full measurements below.';

  const HEIGHT = { min: 120, max: 230 }; /* cm — accepted range */
  const WEIGHT = { min: 30, max: 250 };  /* kg — accepted range */

  /* ---------------------------------------------------------------------
     Helpers
     --------------------------------------------------------------------- */
  const SIZE_ALIASES = {
    XXS: 'XXS', '2XS': 'XXS', XXXS: 'XXS',
    XS: 'XS', EXTRASMALL: 'XS',
    S: 'S', SM: 'S', SMALL: 'S',
    M: 'M', MED: 'M', MEDIUM: 'M',
    L: 'L', LG: 'L', LARGE: 'L',
    XL: 'XL', EXTRALARGE: 'XL',
    XXL: 'XXL', '2XL': 'XXL',
    XXXL: '3XL', '3XL': '3XL',
    XXXXL: '4XL', '4XL': '4XL'
  };

  const canonicalSize = (label) => {
    const key = String(label).toUpperCase().replace(/[^A-Z0-9]/g, '');
    return SIZE_ALIASES[key] || null;
  };

  const looksLikeSize = (label) => {
    if (canonicalSize(label)) return true;
    return /^(one ?size|o\/?s|[0-9]{1,2}(\.5)?)$/i.test(String(label).trim());
  };

  const NUMBER_RE = /-?\d+(?:[.,]\d+)?/g;

  /** Converts every number inside a cell string, preserving separators. */
  const convertCell = (text, from, to) => {
    if (from === to) return text;
    const factor = to === 'in' ? 1 / 2.54 : 2.54;
    const decimals = to === 'in' ? 1 : 0;
    return text.replace(NUMBER_RE, (n) => {
      const value = parseFloat(n.replace(',', '.')) * factor;
      return String(Number(value.toFixed(decimals)));
    });
  };

  const firstNumber = (text) => {
    const m = String(text).match(NUMBER_RE);
    return m ? parseFloat(m[0].replace(',', '.')) : null;
  };

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  /**
   * Which unit did the merchant type the numbers in?
   * Shopify's page editor rewrites pasted HTML (it adds <tbody> and inline
   * styles), and editors that do that often drop unknown attributes — so
   * accept three independent ways of saying it. Centimetres is the default,
   * because that needs no marker at all and therefore cannot be stripped.
   */
  const declaredUnit = (table) => {
    if (table.dataset.unit === 'in' || table.dataset.unit === 'cm') return table.dataset.unit;
    if (table.classList.contains('unit-in')) return 'in';
    if (table.classList.contains('unit-cm')) return 'cm';
    const caption = table.querySelector('caption');
    if (caption && /inch/i.test(caption.textContent)) return 'in';
    return 'cm';
  };

  const formatHeight = (cm, unit) => {
    if (unit !== 'in') return `${Math.round(cm)} cm`;
    const total = Math.round(cm / 2.54);
    return `${Math.floor(total / 12)}′ ${total % 12}″`;
  };

  const formatWeight = (kg, unit) =>
    unit === 'in' ? `${Math.round(kg / 0.45359237)} lb` : `${Math.round(kg)} kg`;

  /* A PDP can show the size guide twice (drawer + accordion), so every
     instance needs its own input ids for the <label for> links to work. */
  let instanceCount = 0;

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  /* ---------------------------------------------------------------------
     Component
     --------------------------------------------------------------------- */
  class SizeChart extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;

      this.sourceTable = this.querySelector('table');
      if (!this.sourceTable) return;

      const parsed = this.parseTable(this.sourceTable);
      if (!parsed) return;

      this.sizes = parsed.sizes;
      this.metrics = parsed.metrics;
      this.sourceUnit = declaredUnit(this.sourceTable);
      this.unit = this.readStoredUnit() || this.sourceUnit;
      this.uid = `msc-sc-${++instanceCount}`;

      this.heightCm = null;
      this.weightKg = null;

      /* How this chart is sized — null means the finder has nothing to go on
         (no chest row and no entry in FIT_BY_CUT) and is not shown. */
      this.fitRule = this.resolveFitRule();
      this.finderAvailable = SHOW_FIT_FINDER && this.fitRule !== null;

      this.recommended = null;

      /* The size guide drawer is rendered inside the variant picker's
         <legend>, i.e. inside <product-variant-selects>, which listens for
         bubbling `change` events to detect a variant selection
         (assets/product-variants.js:115). Our slider fires `change` on
         release; left alone it reads as "customer picked a size", the product
         info re-renders and the drawer is destroyed mid-interaction.
         Every control in here is wired directly, so nothing of ours needs
         these events to bubble any further. */
      ['change', 'input'].forEach((type) =>
        this.addEventListener(type, (event) => event.stopPropagation())
      );

      this.dataset.ready = 'true';
      this.render();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    /* ------------------------------------------------------------------
       Parsing
       ------------------------------------------------------------------ */
    parseTable(table) {
      const grid = Array.from(table.rows).map((row) =>
        Array.from(row.cells).map((cell) => cell.textContent.replace(/\s+/g, ' ').trim())
      );
      const rows = grid.filter((r) => r.length > 1 && r.some((c) => c !== ''));
      if (rows.length < 2) return null;

      const width = Math.max(...rows.map((r) => r.length));
      const normalised = rows.map((r) => {
        const copy = r.slice(0, width);
        while (copy.length < width) copy.push('');
        return copy;
      });

      const header = normalised[0];
      const body = normalised.slice(1);

      const headerIsSizes = header.slice(1).filter(Boolean).every(looksLikeSize);
      const firstColIsSizes = body.map((r) => r[0]).filter(Boolean).every(looksLikeSize);

      let sizes;
      let metrics;

      if (headerIsSizes && !firstColIsSizes) {
        sizes = header.slice(1).filter(Boolean);
        metrics = body
          .filter((r) => r[0])
          .map((r) => ({ label: r[0], values: sizes.map((_, i) => r[i + 1] || '') }));
      } else if (firstColIsSizes) {
        sizes = body.map((r) => r[0]).filter(Boolean);
        metrics = header.slice(1).map((label, i) => ({
          label: label || `Measurement ${i + 1}`,
          values: body.filter((r) => r[0]).map((r) => r[i + 1] || '')
        }));
      } else {
        return null;
      }

      if (!sizes.length || !metrics.length) return null;

      metrics.forEach((m) => {
        m.numeric = m.values.some((v) => firstNumber(v) !== null);
      });

      return { sizes, metrics };
    }

    /* ------------------------------------------------------------------
       Unit preference (best effort — never throws)
       ------------------------------------------------------------------ */
    readStoredUnit() {
      try {
        const stored = window.localStorage.getItem('msc:size-unit');
        return stored === 'cm' || stored === 'in' ? stored : null;
      } catch (e) {
        return null;
      }
    }

    storeUnit(unit) {
      try {
        window.localStorage.setItem('msc:size-unit', unit);
      } catch (e) {
        /* private mode, blocked storage — preference just won't persist */
      }
    }

    /* ------------------------------------------------------------------
       Rendering
       ------------------------------------------------------------------ */
    render() {
      const root = el('div', 'size-chart__ui');

      root.appendChild(this.buildUnitToggle());
      if (this.finderAvailable) root.appendChild(this.buildFinder());
      root.appendChild(this.buildChart());

      this.sourceTable.remove();
      this.insertBefore(root, this.firstChild);

      /* Whatever else the merchant wrote on that page (fit notes, model
         height) stays put, below the interactive part. Hide the wrapper only
         if the table was all it contained. */
      const prose = this.querySelector('.rte');
      if (prose && !prose.textContent.trim() && !prose.querySelector('img, iframe')) {
        prose.hidden = true;
      }

      this.paint();

      this.updateOverflow();
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.updateOverflow());
        this.observer.observe(this);
      }
    }

    buildUnitToggle() {
      const wrap = el('div', 'size-chart__unitbar');
      wrap.appendChild(el('span', 'size-chart__unitlabel', 'Units'));

      const group = el('div', 'size-chart__segmented');
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', 'Measurement units');

      ['cm', 'in'].forEach((unit) => {
        const btn = el('button', 'size-chart__segment', unit.toUpperCase());
        btn.type = 'button';
        btn.dataset.unit = unit;
        btn.setAttribute('aria-pressed', String(unit === this.unit));
        btn.addEventListener('click', () => this.setUnit(unit));
        group.appendChild(btn);
      });

      wrap.appendChild(group);
      this.unitGroup = group;
      return wrap;
    }

    buildFinder() {
      const card = el('div', 'size-chart__finder');

      const head = el('div', 'size-chart__finder-head');
      head.appendChild(el('h3', 'size-chart__finder-title', 'Find your size'));
      head.appendChild(el('p', 'size-chart__finder-sub', 'Enter your height and weight.'));
      card.appendChild(head);

      const fields = el('div', 'size-chart__fields');
      this.heightField = this.buildMeasureField('height', 'Height', true);
      this.weightField = this.buildMeasureField('weight', 'Weight', false);
      fields.appendChild(this.heightField.field);
      fields.appendChild(this.weightField.field);
      card.appendChild(fields);

      const submit = el('button', 'size-chart__submit', 'Find my size');
      submit.type = 'button';
      submit.addEventListener('click', () => { this.commitFields(); this.recommend(); });
      card.appendChild(submit);

      const result = el('div', 'size-chart__result');
      result.setAttribute('aria-live', 'polite');
      result.hidden = true;
      card.appendChild(result);
      this.resultEl = result;

      return card;
    }

    /**
     * A labelled number field. Text, not a slider — every `change` and `input`
     * event is stopped at the component boundary (see connectedCallback), so
     * the variant picker never sees them.
     */
    buildMeasureField(key, label, withSecondary) {
      const field = el('div', 'size-chart__field');

      const labelEl = el('label', 'size-chart__label', label);
      labelEl.htmlFor = `${this.uid}-${key}`;
      field.appendChild(labelEl);

      const row = el('div', 'size-chart__inputs');
      const primary = this.buildInput(`${this.uid}-${key}`, label);
      row.appendChild(primary.wrap);

      /* height needs a second box for feet + inches; weight never does */
      let secondary = null;
      if (withSecondary) {
        secondary = this.buildInput(`${this.uid}-${key}-in`, `${label}, inches`);
        row.appendChild(secondary.wrap);
      }

      field.appendChild(row);
      return { field, primary, secondary };
    }

    buildInput(id, ariaLabel) {
      const wrap = el('span', 'size-chart__inputwrap');

      const input = el('input', 'size-chart__input');
      input.type = 'text';
      input.id = id;
      input.inputMode = 'numeric';
      input.autocomplete = 'off';
      input.setAttribute('aria-label', ariaLabel);
      /* commit on blur / Enter, not per keystroke — clamping "1" to 120 while
         someone is still typing "178" would be maddening */
      input.addEventListener('change', () => this.commitFields());
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); this.commitFields(); this.recommend(); }
      });

      const unit = el('span', 'size-chart__unit');
      wrap.appendChild(input);
      wrap.appendChild(unit);
      return { wrap, input, unit };
    }

    /** Pure parse: fields → metric state. No rendering, no recommending,
        so it is always safe to call from anywhere. */
    readFields() {
      const h = firstNumber(this.heightField.primary.input.value);
      if (h == null) {
        this.heightCm = null;
      } else if (this.unit === 'in') {
        const inches = firstNumber(this.heightField.secondary.input.value) || 0;
        this.heightCm = clamp(h * 12 * 2.54 + inches * 2.54, HEIGHT.min, HEIGHT.max);
      } else {
        this.heightCm = clamp(h, HEIGHT.min, HEIGHT.max);
      }

      const w = firstNumber(this.weightField.primary.input.value);
      if (w == null) {
        this.weightKg = null;
      } else {
        this.weightKg = clamp(this.unit === 'in' ? w * 0.45359237 : w, WEIGHT.min, WEIGHT.max);
      }

    }

    /** A user gesture: parse, re-render the boxes, refresh an existing result.
        Kept separate from readFields() because recommend() also parses, and
        commit → recommend → commit would recurse forever. */
    commitFields() {
      this.readFields();
      this.paintFields();
      if (this.recommended !== null) this.recommend();
    }

    /** State → the input boxes, in whichever unit is active. */
    paintFields() {
      if (!this.finderAvailable) return;
      const imperial = this.unit === 'in';

      this.heightField.secondary.wrap.hidden = !imperial;
      this.heightField.primary.unit.textContent = imperial ? 'ft' : 'cm';
      this.heightField.secondary.unit.textContent = 'in';
      this.weightField.primary.unit.textContent = imperial ? 'lb' : 'kg';

      if (this.heightCm == null) {
        this.heightField.primary.input.value = '';
        this.heightField.secondary.input.value = '';
      } else if (imperial) {
        const total = Math.round(this.heightCm / 2.54);
        this.heightField.primary.input.value = String(Math.floor(total / 12));
        this.heightField.secondary.input.value = String(total % 12);
      } else {
        this.heightField.primary.input.value = String(Math.round(this.heightCm));
      }

      this.weightField.primary.input.value =
        this.weightKg == null
          ? ''
          : String(Math.round(imperial ? this.weightKg / 0.45359237 : this.weightKg));
    }

    buildChart() {
      const wrap = el('div', 'size-chart__chart');
      const head = el('div', 'size-chart__chart-head');
      head.appendChild(el('h3', 'size-chart__chart-title', 'All measurements'));
      this.unitCaption = el('p', 'size-chart__unitcaption');
      head.appendChild(this.unitCaption);
      wrap.appendChild(head);
      this.chartEl = wrap;

      /* One table, every size, every measurement. The size column is pinned
         so it stays readable if the table has to scroll sideways. */
      const scroller = el('div', 'size-chart__tablewrap');
      const table = el('table', 'size-chart__table');

      const thead = el('thead');
      const headRow = el('tr');
      headRow.appendChild(el('th', 'size-chart__th-size', 'Size'));
      this.metrics.forEach((m) => {
        const th = el('th');
        th.scope = 'col';
        th.dataset.metricLabel = m.label;
        headRow.appendChild(th);
      });
      thead.appendChild(headRow);
      table.appendChild(thead);

      const tbody = el('tbody');
      this.sizes.forEach((size, i) => {
        const tr = el('tr');
        tr.dataset.sizeIndex = String(i);
        const th = el('th', 'size-chart__th-size', size);
        th.scope = 'row';
        tr.appendChild(th);
        this.metrics.forEach((m) => {
          const td = el('td');
          td.dataset.raw = m.values[i] || '';
          tr.appendChild(td);
        });
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);
      scroller.appendChild(table);
      wrap.appendChild(scroller);

      this.table = table;
      this.tableWrap = scroller;
      scroller.addEventListener('scroll', () => this.updateOverflow(), { passive: true });

      return wrap;
    }

    /** Flags the fade affordance only when the table really does overflow. */
    updateOverflow() {
      const w = this.tableWrap;
      if (!w) return;
      const overflows = w.scrollWidth - w.clientWidth > 2;
      const atEnd = w.scrollLeft + w.clientWidth >= w.scrollWidth - 2;
      this.classList.toggle('size-chart--scrollable', overflows && !atEnd);
    }

    /* ------------------------------------------------------------------
       State → DOM
       ------------------------------------------------------------------ */
    setUnit(unit) {
      if (unit === this.unit) return;
      this.unit = unit;
      this.storeUnit(unit);

      Array.from(this.unitGroup.children).forEach((btn) =>
        btn.setAttribute('aria-pressed', String(btn.dataset.unit === unit))
      );

      this.paint();
    }

    paint() {
      this.unitCaption.textContent =
        this.unit === 'in' ? 'All measurements in inches' : 'All measurements in centimetres';

      Array.from(this.table.tHead.rows[0].cells).forEach((th, i) => {
        if (i === 0) return;
        th.textContent = this.metrics[i - 1].label;
      });

      Array.from(this.table.tBodies[0].rows).forEach((tr, i) => {
        Array.from(tr.cells).forEach((cell) => {
          if (cell.dataset.raw == null) return;
          const value = convertCell(cell.dataset.raw, this.sourceUnit, this.unit);
          cell.textContent = value || '—';
        });
        tr.classList.toggle('is-recommended', this.recommended === i);
      });

      this.paintFields();
      if (this.tableWrap) this.updateOverflow();
    }

    /* ------------------------------------------------------------------
       The recommendation
       ------------------------------------------------------------------ */
    /** Which rule sizes this chart: per-product entry, else chest-row default. */
    resolveFitRule() {
      let conf = FIT_BY_CUT[CUT_BY_HANDLE[this.dataset.chart || '']];

      /* No handle, or a page we don't know about yet: recognise the chart by
         its own numbers instead of silently falling back to the generic rule. */
      if (!conf) {
        const sig = signature(this.sizes, this.metrics, this.sourceUnit);
        if (sig) {
          conf = Object.keys(FIT_BY_CUT)
            .map((k) => FIT_BY_CUT[k])
            .find((c) => c.sig === sig);
        }
      }

      /* numeric rows we can use, in centimetres */
      const toCm = (metric, flat) =>
        metric
          ? metric.values.map((v) => {
              const n = firstNumber(v);
              if (n == null) return null;
              const cm = this.sourceUnit === 'in' ? n * 2.54 : n;
              /* a chest row is measured flat, i.e. half the circumference */
              return flat && cm < 90 ? cm * 2 : cm;
            })
          : null;

      /* the row that grows with the wearer's height: body length on a top,
         outseam on a bottom — never the sleeve */
      const bodyLengthRow = this.metrics.find(
        (m) =>
          m.numeric &&
          /length|longueur|outseam|inseam/i.test(m.label) &&
          !/sleeve|manche/i.test(m.label)
      );
      const chestRow = this.metrics.find((m) => m.numeric && /chest|poitrine/i.test(m.label));

      const extras = {
        lengthCm: toCm(bodyLengthRow, false),
        chestCm: toCm(chestRow, true),
        lengthRatio: (conf && conf.length) || null,
        room: (conf && conf.room) != null ? conf.room : null,
        verified: (conf && conf.verified) || null
      };

      if (conf && conf.chestUpTo) {
        const limits = this.sizes.map((s) => {
          const key = Object.keys(conf.chestUpTo).find(
            (k) => canonicalSize(k) === canonicalSize(s)
          );
          return key ? conf.chestUpTo[key] : null;
        });
        if (!limits.some((v) => v != null)) return null;
        return Object.assign({ type: 'limits', limits }, extras);
      }

      const chest = this.metrics.find((m) => m.numeric && /chest|poitrine|pit/i.test(m.label));
      if (!chest) return null;
      const garmentCm = chest.values.map((v) => {
        const n = firstNumber(v);
        if (n == null) return null;
        const cm = this.sourceUnit === 'in' ? n * 2.54 : n;
        /* charts are measured flat (half the circumference) */
        return cm < 90 ? cm * 2 : cm;
      });
      if (garmentCm.filter((v) => v != null).length < 2) return null;
      return Object.assign(
        { type: 'ease', garmentCm, ease: conf && conf.ease != null ? conf.ease : DEFAULT_EASE },
        extras
      );
    }

    recommend() {
      this.readFields();
      const heightCm = this.heightCm;
      const weightKg = this.weightKg;

      if (heightCm == null || weightKg == null) {
        this.showResult(
          'error',
          'Almost there',
          this.unit === 'in'
            ? 'Enter your height in feet and inches, and your weight in pounds.'
            : 'Enter your height in centimetres and your weight in kilograms.'
        );
        return;
      }

      const bodyChest =
        (CHEST_K * Math.sqrt(weightKg)) / Math.pow(Math.min(heightCm, CHEST_HEIGHT_CAP), CHEST_HEIGHT_EXP);
      const pick =
        this.fitRule.type === 'ease' ? this.pickByEase(bodyChest) : this.pickByLimits(bodyChest);

      /* Weight decides the chest, height decides the length. A tall shopper can
         come out small at the chest and far too short in the body, so the length
         floor can raise the size — by one step at most, because every step up is
         also more room at the chest. */
      const raised = this.raiseForLength(pick.index, heightCm);
      if (raised !== pick.index) {
        pick.sizedUpForHeight = this.sizes[pick.index];
        pick.index = raised;
        pick.between = null;
        pick.note = null;
      }

      this.recommended = pick.index;
      const size = this.sizes[pick.index];

      /* Nothing about the shopper's own body, and nothing they cannot act on:
         the default line just echoes what they typed. */
      let detail = `Based on ${formatHeight(heightCm, this.unit)} · ${formatWeight(weightKg, this.unit)}.`;

      if (pick.sizedUpForHeight) {
        detail = `Sized up for your height — ${size} gives you the body length this cut is meant to have.`;
      }

      /* One note at most, and only when it is advice the shopper can act on —
         a size either side that they might prefer. Nothing is said about a fit
         problem no size can solve. */
      const notes = [];
      const note = this.lengthNote(pick.index, heightCm) || this.betweenNote(pick.index, pick.between);
      if (note) notes.push(note);

      this.showResult('ok', size, detail, notes);
      this.paint();
    }

    /**
     * Length advice, and only advice. The recommendation already takes the
     * length floor into account, so this fires when the shopper might *prefer*
     * the size either side — and stays quiet when no size would change
     * anything, because telling someone nothing fits them helps nobody.
     */
    lengthNote(index, heightCm) {
      const { lengthCm, lengthRatio } = this.fitRule;
      if (!lengthCm || !lengthRatio || lengthCm[index] == null) return null;

      const ratio = lengthCm[index] / heightCm;
      const [min, max] = lengthRatio;
      const short = ratio < min;
      const long = ratio > max * (1 + LENGTH_SLACK.long);
      if (!short && !long) return null;

      const other = index + (short ? 1 : -1);
      if (lengthCm[other] == null) return null;

      /* only worth mentioning if that size actually lands in the normal range */
      const otherRatio = lengthCm[other] / heightCm;
      if (otherRatio < min || otherRatio > max) return null;

      const by = asUnit(Math.abs(lengthCm[other] - lengthCm[index]), this.unit);
      if (!by) return null;

      return {
        warn: false,
        text: short
          ? `Like a longer body? ${this.sizes[other]} is ${by} longer, and a little roomier with it.`
          : `Like a shorter body? ${this.sizes[other]} is ${by} shorter, and a little closer-fitting.`
      };
    }

    /** Only for a shopper sitting on a size boundary. */
    betweenNote(index, between) {
      if (between == null) return null;
      const { chestCm, lengthCm } = this.fitRule;
      const lo = this.sizes[Math.min(index, between)];
      const hi = this.sizes[Math.max(index, between)];
      const other = this.sizes[between];
      const roomier = between > index;

      const bits = [];
      if (chestCm && chestCm[between] != null && chestCm[index] != null) {
        const d = asUnit(Math.abs(chestCm[between] - chestCm[index]), this.unit);
        if (d) bits.push(`${d} ${roomier ? 'wider' : 'narrower'}`);
      }
      if (lengthCm && lengthCm[between] != null && lengthCm[index] != null) {
        const d = asUnit(Math.abs(lengthCm[between] - lengthCm[index]), this.unit);
        if (d) bits.push(`${d} ${roomier ? 'longer' : 'shorter'}`);
      }

      return {
        warn: false,
        text: `You are right on the line between ${lo} and ${hi}. ${this.sizes[index]} is the intended look; ${other} is${
          bits.length ? ` ${bits.join(' and ')}` : roomier ? ' roomier' : ' closer-fitting'
        }${roomier ? ' for a baggier drape' : ' for a closer drape'}.`
      };
    }

    /**
     * Says what the neighbouring sizes would do, in centimetres of room — and
     * when the shopper sits on a size boundary, says that first.
     */
    neighbourNote(index, bodyChest, between) {
      const { chestCm } = this.fitRule;
      if (!chestCm || chestCm[index] == null) return null;

      const diff = (i) =>
        chestCm[i] == null ? null : Math.abs(chestCm[i] - chestCm[index]);
      const down = index > 0 ? diff(index - 1) : null;
      const up = index + 1 < chestCm.length ? diff(index + 1) : null;

      if (between != null) {
        const other = this.sizes[between];
        const d = diff(between);
        const roomier = between > index;
        return {
          warn: false,
          text: `You are right on the line between ${this.sizes[Math.min(index, between)]} and ${
            this.sizes[Math.max(index, between)]
          }. ${this.sizes[index]} is the intended look; ${other} gives ${
            d != null ? asUnit(d, this.unit) : 'a little'
          } ${roomier ? 'more room and a baggier drape' : 'less room and a closer drape'}.`
        };
      }

      const parts = [];
      if (down != null)
        parts.push(`${this.sizes[index - 1]} takes off about ${asUnit(down, this.unit)} and sits closer`);
      if (up != null)
        parts.push(`${this.sizes[index + 1]} adds about ${asUnit(up, this.unit)} for a baggier look`);
      if (!parts.length) return null;
      return { warn: false, text: `If you want to adjust: ${parts.join('; ')}.` };
    }

    /**
     * Smallest size whose body is long enough for this height, capped at one
     * step above the chest answer. Returns the index to use.
     */
    raiseForLength(index, heightCm) {
      const { lengthCm, lengthRatio } = this.fitRule;
      if (!lengthCm || !lengthRatio) return index;

      /* Same threshold the shopper-facing note uses, so the two never disagree. */
      const floor = lengthRatio[0] * heightCm;
      if (lengthCm[index] != null && lengthCm[index] >= floor) return index;

      /* smallest size that is long enough, at most MAX_SIZE_UP steps up — past
         that the chest would be a tent, and no size really fits that body.
         A step that adds less than a centimetre of body is not worth the width. */
      let best = index;
      for (let i = index + 1; i <= index + MAX_SIZE_UP && i < lengthCm.length; i++) {
        if (lengthCm[i] == null || lengthCm[i] - lengthCm[best] < 1) continue;
        best = i;
        if (lengthCm[i] >= floor) break;
      }
      return best;
    }

    /** Tops: the size whose room at the chest is closest to the intended room. */
    pickByEase(bodyChest) {
      const { garmentCm, ease } = this.fitRule;
      const miss = garmentCm.map((g) => (g == null ? Infinity : Math.abs(g - bodyChest - ease)));

      let index = -1;
      miss.forEach((m, i) => {
        if (m === Infinity) return;
        if (index < 0 || m <= miss[index]) index = i; /* ties → larger size */
      });

      const valid = garmentCm.map((g, i) => (g == null ? -1 : i)).filter((i) => i >= 0);
      const first = valid[0];
      const last = valid[valid.length - 1];
      const room = garmentCm[index] - bodyChest;
      if (index === first && room - ease > OFF_RANGE_CM) return { index, note: 'looser' };
      if (index === last && ease - room > OFF_RANGE_CM) return { index, note: 'tighter' };

      /* nearest neighbour on the other side of the target */
      const side = room > ease ? -1 : 1;
      const pos = valid.indexOf(index) + side;
      const neighbour = valid[pos];
      let between = null;
      if (neighbour != null && Math.abs(miss[neighbour] - miss[index]) < BETWEEN_CM * 2) {
        between = neighbour;
      }
      return { index, between };
    }

    /** Bottoms: first size whose body-chest limit is not exceeded. */
    pickByLimits(bodyChest) {
      const { limits } = this.fitRule;
      const valid = limits.map((l, i) => i);
      let index = valid.find((i) => limits[i] != null && bodyChest <= limits[i]);
      if (index == null) index = this.sizes.length - 1;

      let between = null;
      if (limits[index] != null && limits[index] - bodyChest < BETWEEN_CM && index + 1 < this.sizes.length) {
        between = index + 1;
      } else if (index > 0 && limits[index - 1] != null && bodyChest - limits[index - 1] < BETWEEN_CM) {
        between = index - 1;
      }
      return { index, between };
    }

    showResult(state, headline, detail, notes) {
      this.resultEl.hidden = false;
      this.resultEl.className = `size-chart__result size-chart__result--${state}`;
      this.resultEl.textContent = '';

      if (state === 'ok') {
        this.resultEl.appendChild(el('span', 'size-chart__result-label', 'Your size'));
      }
      this.resultEl.appendChild(el('strong', 'size-chart__result-size', headline));
      this.resultEl.appendChild(el('span', 'size-chart__result-detail', detail));

      if (state === 'ok') {
        this.resultEl.appendChild(el('span', 'size-chart__result-foot', FOOTNOTE));
      }

      (notes || []).forEach((note) => {
        this.resultEl.appendChild(
          el(
            'span',
            `size-chart__result-note${note.warn ? ' size-chart__result-note--warn' : ''}`,
            note.text
          )
        );
      });
    }
  }

  customElements.define('size-chart', SizeChart);
})();